import { type EventSourceMessage, fetchEventSource } from '@microsoft/fetch-event-source';
import EventBus from '@/services/eventbus/EventBus';
import MercureMessageEvent from '@/services/keyvue-api/mercure/MercureMessageEvent';
import MercureConnectionLostEvent, { type MercureConnectionLostEventParameters } from '@/services/keyvue-api/mercure/MercureConnectionLostEvent';
import MercureConnectionOpenedEvent, { type MercureConnectionOpenedEventParameters } from '@/services/keyvue-api/mercure/MercureConnectionOpenedEvent';

interface Token {
    hub: string,
    topic: string,
    token: string,
}

export default class MercureConnection {
    static connections: Record<string, MercureConnection> = {};

    static getInstance(name: string): MercureConnection {
        if (!MercureConnection.connections[name]) {
            MercureConnection.connections[name] = new MercureConnection(name);
        }

        return MercureConnection.connections[name];
    }

    private token?: Token;

    private tokenCallBack?: () => Promise<Token>;

    private name: string;

    private numberOfFails = 0;

    private connection: Promise<void> | null = null;

    private controller: AbortController;

    constructor(name: string) {
        this.name = name;
        this.controller = new AbortController();

        this.createWindowFacade();
        this.loadToken();
    }

    private createWindowFacade() {
        /* For debugging purposes we expose some code in dev */
        if (import.meta.env.MODE === 'development') {
            // eslint-disable-next-line
            // @ts-ignore
            if (!window.InveslMercureConnection) {
                // eslint-disable-next-line
                // @ts-ignore
                window.InveslMercureConnection = {};
            }

            // eslint-disable-next-line
            // @ts-ignore
            window.InveslMercureConnection[this.name] = {
                abort: () => {
                    this.abort();
                },
                getConnection: () => {
                    return this.connection;
                },
            };
        }
    }

    public setTokenCallback(callback: () => Promise<Token>): void {
        this.tokenCallBack = callback;
    }

    private loadToken() {
        const sessionToken = window.sessionStorage.getItem(this.sessionKey);

        if (sessionToken) {
            this.setToken(JSON.parse(sessionToken));
        }
    }

    private setToken(token: Token) {
        this.token = token;
        window.sessionStorage.setItem(this.sessionKey, JSON.stringify(this.token));
    }

    private clearToken(): void {
        window.sessionStorage.removeItem(this.sessionKey);
        this.token = undefined;
    }

    public async ensureConnection() {
        if (!this.tokenCallBack) {
            throw new Error('Connection creation not available');
        }

        /** we already have an open connection */
        if (this.connection !== null) {
            return;
        }

        if (!this.token) {
            this.setToken(await this.tokenCallBack());
        }

        if (!this.token) {
            throw new Error('No token set');
        }

        try {
            if (import.meta.env.MODE === 'development') {
                // eslint-disable-next-line no-console
                console.info(`Starting a EventSourceStream: ${this.name}`);
            }

            this.connection = fetchEventSource(this.topicUrl, {
                headers: {
                    Authorization: `Bearer ${this.token.token}`,
                },
                signal: this.controller.signal,
                onopen: this.onOpen.bind(this),
                onmessage: this.onMessage.bind(this),
                onclose: this.onClose.bind(this),
                openWhenHidden: true,
            }).finally(() => {
                this.setConnectionLost();
            });

            this.numberOfFails = 0;
        } catch (error) {
            await this.onError(error);
        }
    }

    private async onOpen() {
        const eventBus = EventBus.getInstance();
        eventBus.dispatch<MercureConnectionOpenedEventParameters>(MercureConnectionOpenedEvent(this.name), {
            connectionName: this.name,
        });
    }

    // eslint-disable-next-line class-methods-use-this
    private async onMessage(message: EventSourceMessage): Promise<void> {
        const eventBus = EventBus.getInstance();
        eventBus.dispatch(MercureMessageEvent(this.name, message.event), JSON.parse(message.data));
    }

    private async onClose() {
        this.numberOfFails += 1;

        if (this.numberOfFails > 1) {
            this.abort();
            return;
        }

        // restart connection
        await this.ensureConnection();
    }

    private async onError(error: any) {
        this.numberOfFails += 1;

        if (this.numberOfFails > 1) {
            this.abort();
            // eslint-disable-next-line no-console
            console.warn(error, 'test');
            return;
        }

        // restart connection
        await this.ensureConnection();
    }

    /**
     * Does not trigger onClose
     * Does trigger setConnectionLost
     * */
    public abort(): void {
        this.controller.abort();
    }

    private setConnectionLost() {
        this.connection = null;
        this.clearToken();

        if (import.meta.env.MODE === 'development') {
            // eslint-disable-next-line no-console
            console.info(`Connection for EventSourceStream: ${this.name} has closed`);
        }

        const eventBus = EventBus.getInstance();
        eventBus.dispatch<MercureConnectionLostEventParameters>(MercureConnectionLostEvent(this.name), {
            connectionName: this.name,
        });
    }

    private get sessionKey() {
        return `invesl-mercure-connection--${this.name}`;
    }

    private get topicUrl() {
        if (!this.token) {
            throw new Error('No token available');
        }

        const url = new URL(this.token.hub);
        url.searchParams.append('topic', this.token.topic);
        return url.toString();
    }
}
